// Заявки › pure helpers: what state a row is in, when it is due, and how a batch adds up.
//
// "Due" is computed the way the pipeline computes it (02 §4.1): never run means due NOW — a fresh
// install backfills rather than sitting idle — otherwise cooldown plus this query's own jitter.

import type { QueryRow } from './useAdmin.js';

export type QueryState = 'draft' | 'paused' | 'active';

export const STATE_LABEL: Readonly<Record<QueryState, string>> = {
  draft: 'чернова',
  paused: 'спряна',
  active: 'активна',
};

/** draft = never approved; paused = approved once, switched off since. */
export function stateOf(row: QueryRow): QueryState {
  if (row.active) return 'active';
  return row.approved_at === null ? 'draft' : 'paused';
}

export function dueState(row: QueryRow): { text: string; due: boolean } {
  if (row.last_run_at === null) return { text: 'никога — предстои', due: true };

  const days = (Date.now() - new Date(row.last_run_at).getTime()) / 86_400_000;
  const wait = row.cooldown_days + row.cooldown_jitter_days;
  const left = Math.ceil(wait - days);

  if (left <= 0) return { text: 'предстои', due: true };
  return { text: `след ${left} дни`, due: false };
}

export interface BatchSummary {
  batch_id: string;
  vertical_key: string;
  city_key: string;
  total: number;
  draft: number;
  paused: number;
  active: number;
}

export function summarizeBatches(rows: readonly QueryRow[]): BatchSummary[] {
  const byId = new Map<string, BatchSummary>();
  for (const row of rows) {
    if (row.batch_id === null) continue;
    const entry = byId.get(row.batch_id) ?? {
      batch_id: row.batch_id,
      vertical_key: row.vertical_key,
      city_key: row.city_key,
      total: 0,
      draft: 0,
      paused: 0,
      active: 0,
    };
    entry.total++;
    entry[stateOf(row)]++;
    byId.set(row.batch_id, entry);
  }
  return [...byId.values()].sort((a, b) => b.draft - a.draft || b.total - a.total);
}

/** Active rows that would run tonight, per vertical — the header's budget view (plan §6.11). */
export function dueByVertical(rows: readonly QueryRow[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows) {
    if (!row.active || !dueState(row).due) continue;
    out.set(row.vertical_key, (out.get(row.vertical_key) ?? 0) + 1);
  }
  return out;
}

export function shortBatch(id: string): string {
  return id.slice(0, 8);
}
